import React, { useState } from 'react';
import { useUserProfile } from '../../modules/auth/hooks/useUserProfile';
import type { UserProfileData } from '../../modules/auth/services/user-profile.service';

export const UserProfile: React.FC = () => {
  const { profileData, isLoading, error, saveProfile, reloadProfile } = useUserProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<UserProfileData | null>(null);

  // Sincronizar datos editados con los datos del perfil cuando no esté editando
  React.useEffect(() => {
    if (!isEditing && profileData) {
      setEditedData(profileData);
    }
  }, [isEditing, profileData]);

  const handleInputChange = (field: keyof UserProfileData, value: string) => {
    if (!editedData) return;
    
    setEditedData(prev => ({
      ...prev!,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!editedData) return;

    try {
      await saveProfile(editedData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      // TODO: Mostrar mensaje de error al usuario
    }
  };

  const handleCancel = async () => {
    setIsEditing(false);
    await reloadProfile();
  };



  if (isLoading && !profileData) {
    return (
      <div className="max-w-4xl mx-auto p-6 flex items-center justify-center min-h-96">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-4xl text-emerald-600 mb-4"></i>
          <p className="text-gray-600">Cargando perfil de usuario...</p>
        </div>
      </div>
    );
  }

  if (!profileData || !editedData) {
    return (
      <div className="max-w-4xl mx-auto p-6 flex items-center justify-center min-h-96">
        <div className="text-center">
          <p className="text-gray-600">No se pudo cargar el perfil de usuario</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mi Perfil</h1>
          <p className="text-gray-600 mt-1">Gestiona tu información personal y preferencias</p>
        </div>
        
        <div className="flex items-center space-x-2">
          {isEditing && (
            <button
              onClick={handleCancel}
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              <i className="ri-close-line"></i>
              <span>Cancelar</span>
            </button>
          )}
          
          <button
            onClick={() => isEditing ? handleSave() : setIsEditing(true)}
            disabled={isLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              <i className="ri-loader-4-line animate-spin"></i>
            ) : (
              <i className={`${isEditing ? 'ri-save-line' : 'ri-edit-line'}`}></i>
            )}
            <span>
              {isLoading ? 'Guardando...' : (isEditing ? 'Guardar' : 'Editar')}
            </span>
          </button>
        </div>
      </div>

      {/* User Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4">
          {/* Avatar */}
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center">
            <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center text-white text-xl font-bold">
              <i className="ri-user-3-line"></i>
            </div>
          </div>
          
          {/* User Info */}
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">
              {profileData.nombre} {profileData.apellido}
            </h2>
            <p className="text-gray-600">{editedData.email}</p>
            <div className="mt-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800">
                Plan Premium
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Información Personal</h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Nombre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre
            </label>
            {isEditing ? (
              <input
                type="text"
                value={editedData.nombre}
                onChange={(e) => handleInputChange('nombre', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            ) : (
              <p className="text-gray-900">{editedData.nombre}</p>
            )}
          </div>

          {/* Apellido */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Apellido
            </label>
            {isEditing ? (
              <input
                type="text"
                value={editedData.apellido}
                onChange={(e) => handleInputChange('apellido', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            ) : (
              <p className="text-gray-900">{editedData.apellido}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            {isEditing ? (
              <input
                type="email"
                value={editedData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            ) : (
              <p className="text-blue-600">{editedData.email}</p>
            )}
          </div>

          {/* Teléfono */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Teléfono
            </label>
            {isEditing ? (
              <input
                type="tel"
                value={editedData.telefono}
                onChange={(e) => handleInputChange('telefono', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            ) : (
              <p className="text-gray-900">{editedData.telefono}</p>
            )}
          </div>

          {/* Fecha de Nacimiento */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha de Nacimiento
            </label>
            {isEditing ? (
              <input
                type="text"
                value={editedData.fechaNacimiento}
                onChange={(e) => handleInputChange('fechaNacimiento', e.target.value)}
                placeholder="DD/MM/YYYY"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            ) : (
              <p className="text-gray-900">{editedData.fechaNacimiento}</p>
            )}
          </div>

          {/* Ocupación */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ocupación
            </label>
            {isEditing ? (
              <input
                type="text"
                value={editedData.ocupacion}
                onChange={(e) => handleInputChange('ocupacion', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            ) : (
              <p className="text-gray-900">{editedData.ocupacion}</p>
            )}
          </div>
        </div>
      </div>

      {/* Work Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Información Laboral</h3>
        
        <div className="space-y-6">
          {/* Empresa */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Empresa
            </label>
            {isEditing ? (
              <input
                type="text"
                value={editedData.empresa}
                onChange={(e) => handleInputChange('empresa', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            ) : (
              <p className="text-blue-600">{editedData.empresa}</p>
            )}
          </div>

          {/* Dirección */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dirección
            </label>
            {isEditing ? (
              <input
                type="text"
                value={editedData.direccion}
                onChange={(e) => handleInputChange('direccion', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            ) : (
              <p className="text-gray-900">{editedData.direccion}</p>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons (only show when editing) */}
      {isEditing && (
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Guardar Cambios
          </button>
        </div>
      )}
    </div>
  );
};
